import TileContentMaker from 'Components/DashboardTile/TileContentMaker';
import PlatformConditional from 'Components/PlatformConditional';
import { isNumber, isPartialDate } from 'neuro-utils';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { formatPartialDate } from 'Utility/date';
import { sortAndTakeLatest } from 'Utility/documentHandling';

import type { TDocument } from '../utils';
import type { IData } from 'Components/DashboardTile/TileContentMaker/components/definitions';

const fm = (id: string, values?: { [key: string]: string | number | undefined }): React.JSX.Element => (
  <FormattedMessage id={id} values={values} />
);

const formatDate = (d: PartialDate | null | undefined) => (isPartialDate(d) ? formatPartialDate(d) : '');

const ClinicalStudyDash = ({ documents }: IOwnProps): React.JSX.Element => {
  const smadmdDocTypes = ['fvc', 'enmg', 'nocturnalPolygraphy'] satisfies TDocumentType[];
  const msDocTypes = [
    'vep',
    'sep',
    'baep',
    'octscan',
    'ecg',
    'echocardiography',
    'bloodPressure',
    'heartRate',
  ] satisfies TDocumentType[];
  const polyneuroDocTypes = ['enmg'] satisfies TDocumentType[];

  const msData: IData[] = [];

  sortAndTakeLatest(msDocTypes, documents).forEach((d, i) => {
    switch (d._type) {
      case 'vep':
        msData.push({ date: formatDate(d.date), title: fm('clinicalstudy.vep'), key: 'vep' + i });
        break;
      case 'sep':
        msData.push({ date: formatDate(d.date), title: fm('clinicalstudy.sep'), key: 'sep' + i });
        break;
      case 'baep':
        msData.push({ date: formatDate(d.date), title: fm('clinicalstudy.baep'), key: 'baep' + i });
        break;
      case 'octscan':
        msData.push({
          date: formatDate(d.date),
          title: fm('clinicalstudy.octscan'),
          key: 'octscan' + i,
          value: d.result ? fm(`clinicalstudy.opts.normalBorderlineAbnormal.${d.result}`) : '-',
        });
        break;
      case 'ecg':
        msData.push({
          date: formatDate(d.date),
          title: fm('clinicalstudy.ecg'),
          key: 'ecg' + i,
          value: d.result ? fm(`clinicalstudy.opts.normalBorderlineAbnormal.${d.result}`) : '-',
        });
        break;
      case 'echocardiography':
        msData.push({
          date: formatDate(d.date),
          title: fm('clinicalstudy.echocardiographyShort'),
          key: 'echocardiographyShort' + i,
          value: d.result ? fm(`clinicalstudy.opts.normalBorderlineAbnormal.${d.result}`) : '-',
        });
        break;
      case 'bloodPressure':
        msData.push({
          date: formatDate(d.date),
          title: fm('clinicalstudy.bloodPressure'),
          key: 'bloodPressure' + i,
          value: (
            <div>
              {fm('clinicalstudy.bloodPressureUnit', {
                n: (d.bloodPressureSystolic ?? '-') + ' / ' + (d.bloodPressureDiastolic ?? '-'),
              })}
            </div>
          ),
        });
        break;
      case 'heartRate':
        msData.push({
          date: formatDate(d.date),
          title: fm('clinicalstudy.heartRate'),
          key: 'heartRate' + i,
          value: isNumber(d.heartRate) ? fm('clinicalstudy.heartRateUnit', { n: d.heartRate }) : '-',
        });
        break;
      default:
        break;
    }
  });

  return (
    <React.Fragment>
      <PlatformConditional platform={['sma', 'dmd']}>
        <TileContentMaker
          type="recentList"
          data={sortAndTakeLatest(smadmdDocTypes, documents).map((d, i) => {
            switch (d._type) {
              case 'fvc': {
                return {
                  date: isPartialDate(d.date) ? formatPartialDate(d.date) : '',
                  title: fm('clinicalstudy.fvcShort'),
                  value: (
                    <div>
                      {isNumber(d.litres) ? fm('clinicalstudy.litreUnit', { n: d.litres }) : '-'}
                      {' / '}
                      {isNumber(d.predicted) ? fm('clinicalstudy.percentageUnit', { n: d.predicted }) : '-'}
                    </div>
                  ),
                  key: 'fvc' + i,
                };
              }
              case 'enmg': {
                return {
                  date: isPartialDate(d.date) ? formatPartialDate(d.date) : '',
                  title: fm('clinicalstudy.enmg'),
                  value: <>&nbsp;</>,
                  key: 'enmg' + i,
                };
              }
              case 'nocturnalPolygraphy': {
                return {
                  date: isPartialDate(d.date) ? formatPartialDate(d.date) : '',
                  title: fm('clinicalstudy.nocturnalPolygraphy'),
                  value: <>&nbsp;</>,
                  key: 'nocturnalPolygraphy' + i,
                };
              }
            }
          })}
        />
      </PlatformConditional>
      <PlatformConditional platform={'ms'}>
        <TileContentMaker type="recentList" data={msData} />
      </PlatformConditional>

      <PlatformConditional platform={['polyneuro']}>
        <TileContentMaker
          type="recentList"
          data={sortAndTakeLatest(polyneuroDocTypes, documents).map((d, i) => {
            switch (d._type) {
              case 'enmg': {
                return {
                  date: isPartialDate(d.date) ? formatPartialDate(d.date) : '',
                  title: fm('clinicalstudy.enmg'),
                  value: <>&nbsp;</>,
                  key: 'enmg' + i,
                };
              }
            }
          })}
        />
      </PlatformConditional>
    </React.Fragment>
  );
};

interface IOwnProps {
  documents: Array<TDocument>;
}

export default ClinicalStudyDash;
